import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import emailjs from 'emailjs-com';
import { BsWhatsapp, BsGithub, BsLinkedin } from 'react-icons/bs';
import { MdOutlineMailLock } from 'react-icons/md';
import {
  FiSend, FiCheck, FiAlertCircle, FiArrowUpRight,
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { images } from '../../constants';
import useWaterRipples from './useWaterRipples';

// ─── Status badge ──────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    sending: {
      icon: null,
      text: 'Sending…',
      cls: 'bg-white/8 text-white/60 border-white/10',
    },
    success: {
      icon: <FiCheck className="w-3.5 h-3.5" />,
      text: "Sent! I'll be in touch soon.",
      cls: 'bg-emerald-500/12 text-emerald-300 border-emerald-500/25',
    },
    error: {
      icon: <FiAlertCircle className="w-3.5 h-3.5" />,
      text: 'Something went wrong — retry',
      cls: 'bg-rose-500/12 text-rose-300 border-rose-500/25',
    },
  };
  const s = map[status];
  if (!s) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: -6, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -6, scale: 0.97 }}
      transition={{ duration: 0.25 }}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl border text-xs font-medium tracking-wide ${s.cls}`}
    >
      {s.icon}
      {s.text}
    </motion.div>
  );
};
StatusBadge.propTypes = { status: PropTypes.oneOf(['sending', 'success', 'error']).isRequired };

// ─── Premium Glass Input ────────────────────────────────────────────────────
const GlassInput = ({
  id, label, type, name, placeholder, required, rows,
}) => (
  <div className="group flex flex-col gap-2">
    <label
      htmlFor={id}
      className="text-[10px] font-extrabold tracking-[0.22em] uppercase text-white/60 group-focus-within:text-rose-300 transition-colors duration-300"
    >
      {label}
    </label>
    {rows ? (
      <textarea
        id={id}
        name={name}
        placeholder={placeholder}
        required={required}
        rows={rows}
        className="w-full bg-black/30 border border-white/20 rounded-[1.15rem] px-4 py-4 text-sm text-white placeholder:text-white/40 outline-none focus:border-rose-400/55 focus:bg-black/40 focus:shadow-[0_0_0_4px_rgba(244,63,94,0.08),0_8px_30px_rgba(0,0,0,0.18)] transition-all duration-300 resize-none leading-relaxed"
      />
    ) : (
      <input
        id={id}
        type={type}
        name={name}
        placeholder={placeholder}
        required={required}
        className="w-full bg-black/30 border border-white/20 rounded-[1.15rem] px-4 py-4 text-sm text-white placeholder:text-white/40 outline-none focus:border-rose-400/55 focus:bg-black/40 focus:shadow-[0_0_0_4px_rgba(244,63,94,0.08),0_8px_30px_rgba(0,0,0,0.18)] transition-all duration-300"
      />
    )}
  </div>
);
GlassInput.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  type: PropTypes.string,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  rows: PropTypes.number,
};
GlassInput.defaultProps = {
  type: 'text',
  placeholder: '',
  required: false,
  rows: undefined,
};

// ─── Availability pulse ─────────────────────────────────────────────────────
const AvailabilityDot = () => (
  <span className="relative flex h-2.5 w-2.5">
    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
  </span>
);

// ─── Contact method row ─────────────────────────────────────────────────────
const ContactRow = ({
  href, icon, label, value, delay,
}) => (
  <motion.a
    href={href}
    target="_blank"
    rel="noreferrer"
    initial={{ opacity: 0, x: -12 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    className="group flex items-center gap-4 py-4 border-b border-white/[0.12] hover:border-white/[0.25] hover:translate-x-1 transition-all duration-300 cursor-pointer"
  >
    <div className="flex-shrink-0 w-10 h-10 rounded-2xl bg-black/40 border border-white/20 flex items-center justify-center text-rose-300/90 group-hover:text-rose-300 group-hover:bg-rose-500/[0.15] group-hover:border-rose-400/40 transition-all duration-300 shadow-[0_6px_18px_rgba(0,0,0,0.25)]">
      {icon}
    </div>
    <div className="flex flex-col min-w-0 flex-1">
      <span className="text-[10px] tracking-[0.2em] uppercase text-white/50 font-bold">{label}</span>
      <span className="text-sm text-white/90 group-hover:text-white transition-colors duration-300 truncate mt-0.5">{value}</span>
    </div>
    <FiArrowUpRight className="w-3.5 h-3.5 text-white/30 group-hover:text-rose-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300 flex-shrink-0" />
  </motion.a>
);
ContactRow.propTypes = {
  href: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  delay: PropTypes.number.isRequired,
};

// ─── Trust chip ─────────────────────────────────────────────────────────────
const TrustChip = ({ children }) => (
  <span className="px-3 py-1.5 rounded-full border border-white/20 bg-black/30 text-[11px] text-white/70">
    {children}
  </span>
);
TrustChip.propTypes = { children: PropTypes.node.isRequired };

// ─── MAIN FOOTER ───────────────────────────────────────────────────────────
const Footer = () => {
  const rootRef = useRef(null);
  const canvasRef = useRef(null);
  const form = useRef();
  const [sendStatus, setSendStatus] = useState(null);

  // Same tuning as techlion.co.ke (jQuery Ripples: resolution 500, dropRadius 20, perturbance 0.04)
  const rippleStatus = useWaterRipples(canvasRef, rootRef, images.bg5, {
    resolution: 512,
    dropRadius: 20,
    perturbance: 0.04,
  });

  const sendEmail = async (e) => {
    e.preventDefault();
    setSendStatus('sending');
    try {
      await emailjs.sendForm(
        'service_5ffrk7i',
        'template_srfp28g',
        form.current,
        'ebSiQgV_A4nv5pegs',
      );
      setSendStatus('success');
      e.target.reset();
      setTimeout(() => setSendStatus(null), 6000);
    } catch {
      setSendStatus('error');
      setTimeout(() => setSendStatus(null), 6000);
    }
  };

  return (
    <div
      ref={rootRef}
      className="relative w-full h-screen overflow-hidden flex flex-col bg-cover bg-center"
      // Static image shows while WebGL boots, and stays as the fallback if it can't
      style={{ backgroundColor: '#06090c', backgroundImage: `url(${images.bg5})` }}
    >
      {/* WebGL canvas */}
      <canvas
        ref={canvasRef}
        aria-hidden
        className={`absolute inset-0 z-0 w-full h-full pointer-events-none transition-opacity duration-700 ${rippleStatus === 'ready' ? 'opacity-100' : 'opacity-0'}`}
      />

      {/* FIX 3: Strong dark overlay — matches the reference screenshot's dark
          semi-transparent treatment so all text is clearly legible */}
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{
          background: `
            linear-gradient(
              to bottom,
              rgba(4,6,10,0.70) 0%,
              rgba(4,6,10,0.56) 30%,
              rgba(4,6,10,0.54) 65%,
              rgba(4,6,10,0.76) 100%
            )
          `,
        }}
      />

      {/* Extra shade behind the left-column copy, which sits over the bright windows */}
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{ background: 'linear-gradient(to right, rgba(4,6,10,0.45) 0%, rgba(4,6,10,0.2) 40%, transparent 60%)' }}
      />

      {/* Subtle rose accent glow */}
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 70% 52%, rgba(244,63,94,0.07) 0%, transparent 60%)',
        }}
      />

      <div className="relative z-20 flex flex-col h-full overflow-y-auto">
        {/* Top strip */}
        {/* Centred so it clears the fixed menu (left) and social buttons (right) */}
        <div className="shrink-0 flex h-[76px] items-center justify-center px-24 border-b border-white/[0.12] bg-black/50">
          <div className="flex items-center gap-3">
            <AvailabilityDot />
            <span className="text-[10px] font-bold tracking-[0.24em] uppercase text-white/70">
              Available for work
            </span>
          </div>
        </div>

        {/* Main */}
        <div className="flex flex-col lg:flex-row flex-1 min-h-0">
          {/* Left panel */}
          <div className="relative flex flex-col lg:w-[46%] px-7 sm:px-12 pt-12 pb-10 lg:border-r border-white/[0.10] overflow-y-auto">
            <div className="mb-8">
              <p className="text-[9px] tracking-[0.35em] uppercase text-rose-300/90 mb-4 font-bold">
                Let&apos;s collaborate
              </p>

              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/20 bg-black/40 mb-5">
                <span className="h-2 w-2 rounded-full bg-emerald-400" />
                <span className="text-[10px] uppercase tracking-[0.22em] text-white/80 font-semibold">
                  Full-Stack Engineer · React · Node.js
                </span>
              </div>

              <h2 className="text-[2.85rem] sm:text-[3.5rem] xl:text-[4.15rem] font-extrabold tracking-[-0.045em] leading-[0.92] text-white mb-5">
                Get in
                {' '}
                <motion.span
                  className="relative inline-block"
                  animate={{ opacity: [1, 0.78, 1] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <span
                    className="text-rose-400 italic font-semibold"
                    style={{ fontFamily: 'var(--font-display)' }}
                  >
                    touch
                  </span>
                  <motion.span
                    className="absolute -bottom-1.5 left-0 h-[2px] bg-gradient-to-r from-rose-400 to-transparent"
                    initial={{ width: 0 }}
                    animate={{ width: '100%' }}
                    transition={{ delay: 0.8, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  />
                </motion.span>
              </h2>

              <div className="w-16 h-px bg-gradient-to-r from-rose-400/80 to-transparent mb-6" />

              <p className="text-sm sm:text-[15px] text-white/80 leading-8 max-w-[29rem]">
                Whether you have a role to fill, a product to build, or an idea worth exploring,
                I&apos;m open to meaningful collaborations, ambitious teams, and work that values
                thoughtful user experience.
              </p>
            </div>

            <div className="flex flex-col mb-8">
              <ContactRow
                href="mailto:mussocollins@gmail.com"
                icon={<MdOutlineMailLock className="w-4 h-4" />}
                label="Email"
                value="mussocollins@gmail.com"
                delay={0.25}
              />
              <ContactRow
                href="https://api.whatsapp.com/send?phone=254717684174"
                icon={<BsWhatsapp className="w-4 h-4" />}
                label="WhatsApp"
                value="+254 717 684 174"
                delay={0.32}
              />
              <ContactRow
                href="https://github.com/Carshy"
                icon={<BsGithub className="w-4 h-4" />}
                label="GitHub"
                value="github.com/Carshy"
                delay={0.39}
              />
              <ContactRow
                href="https://www.linkedin.com/in/collins-musoko/"
                icon={<BsLinkedin className="w-4 h-4" />}
                label="LinkedIn"
                value="collins-musoko"
                delay={0.46}
              />
            </div>

            <div className="mt-auto self-start flex items-center gap-3 px-4 py-2.5 rounded-2xl border border-white/20 bg-black/50 shadow-[0_8px_28px_rgba(0,0,0,0.35)]">
              <div className="w-7 h-7 rounded-full border border-emerald-500/40 bg-emerald-500/15 flex items-center justify-center">
                <FiCheck className="w-3.5 h-3.5 text-emerald-300" />
              </div>
              <div className="flex flex-col">
                <span className="text-[9px] text-white/50 tracking-widest uppercase font-bold">Avg. response</span>
                <span className="text-sm font-bold text-white/90">&lt; 24 hours</span>
              </div>
            </div>

            <div className="hidden lg:block absolute bottom-8 left-8 opacity-[0.05] pointer-events-none">
              <img
                src={images.shakes}
                alt=""
                className="w-24 h-24 object-contain"
                style={{ filter: 'invert(1)' }}
              />
            </div>
          </div>

          {/* Right panel */}
          <div className="flex flex-col flex-1 px-7 sm:px-12 pt-12 pb-10 overflow-y-auto">
            <div className="flex-1 flex flex-col p-6 sm:p-9 rounded-[2rem] border border-white/20 bg-black/45 backdrop-blur-md shadow-[0_20px_80px_rgba(0,0,0,0.5)] relative overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none" />
              <div className="absolute -top-20 right-[-40px] w-48 h-48 rounded-full bg-white/10 blur-3xl opacity-20 pointer-events-none" />
              <div className="absolute -bottom-16 left-[-20px] w-40 h-40 rounded-full bg-rose-500/10 blur-3xl opacity-20 pointer-events-none" />
              <div className="absolute inset-y-0 left-0 w-px bg-gradient-to-b from-transparent via-white/[0.12] to-transparent pointer-events-none" />

              <p className="text-[10px] tracking-[0.3em] uppercase text-white/60 font-black mb-7">
                Drop a message
              </p>

              <form ref={form} onSubmit={sendEmail} className="flex flex-col gap-5 flex-1" noValidate>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <GlassInput id="f-name" label="Full name" name="name" placeholder="Collins Musoko" required />
                  <GlassInput id="f-email" label="Email address" type="email" name="email" placeholder="hello@example.com" required />
                </div>
                <GlassInput id="f-subj" label="Subject" name="subject" placeholder="Role, project, or just saying hi…" />
                <GlassInput id="f-msg" label="Message" name="message" placeholder="Tell me what you're working on…" required rows={4} />

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-2">
                  <button
                    type="submit"
                    disabled={sendStatus === 'sending'}
                    className="group relative flex items-center gap-2.5 px-8 py-3.5 rounded-2xl bg-rose-500 hover:bg-rose-400 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-black tracking-wide overflow-hidden transition-all duration-300 shadow-[0_12px_36px_rgba(244,63,94,0.28)] hover:shadow-[0_16px_42px_rgba(244,63,94,0.35)] focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-300"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 pointer-events-none" />
                    <FiSend className="w-4 h-4 relative group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                    <span className="relative">{sendStatus === 'sending' ? 'Sending…' : 'Send Message'}</span>
                  </button>
                  <AnimatePresence mode="wait">
                    {sendStatus && <StatusBadge key={sendStatus} status={sendStatus} />}
                  </AnimatePresence>
                </div>

                <div className="flex flex-wrap items-center gap-3 mt-2">
                  <TrustChip>Usually replies within 24 hours</TrustChip>
                  <TrustChip>Open to freelance & full-time</TrustChip>
                  <TrustChip>Nairobi, Kenya</TrustChip>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="shrink-0 border-t border-white/[0.12] bg-black/50">
          <div className="max-w-7xl mx-auto px-5 sm:px-10 py-3.5 flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-[9px] text-white/50 tracking-[0.2em] uppercase order-2 sm:order-1">
              © 2026 Collins Musoko · All rights reserved
            </p>
            <p className="text-[9px] text-white/35 order-3">
              Built with React · Tailwind · WebGL
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

TrustChip.displayName = 'TrustChip';
ContactRow.displayName = 'ContactRow';
GlassInput.displayName = 'GlassInput';
StatusBadge.displayName = 'StatusBadge';

export default Footer;
